import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { api } from '../api/endpoints';
import type { PaymentMode, StaffTab, StaffTabLine, StaffTabSummary } from '../api/types';
import { formatDateTime, formatMoney } from '../lib/format';
import { MenuPickerDialog, PaymentModePicker, SplitFields } from '../components/CustomerTab';
import { useToast } from '../components/Toast';
import { Badge, Button, Card, EmptyState, ErrorNote, Modal, Spinner } from '../components/ui';

// What staff took from the menu on shift. Anyone at the counter can add to
// anyone's tab; items are charged at what the cafe paid for them, and the
// tab is settled in one go. Reports keep this out of revenue.

const roundMoney = (value: number) => Math.round(value * 100) / 100;

function PayStaffDialog({
  tab,
  onClose,
  onPaid,
}: {
  tab: StaffTab;
  onClose: () => void;
  onPaid: (tab: StaffTab) => void;
}) {
  const toast = useToast();
  const [mode, setMode] = useState<PaymentMode>('cash');
  const [cashPart, setCashPart] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const cashPartValue = cashPart.trim() === '' ? 0 : Number(cashPart);
  const upiPartValue = roundMoney(tab.due_amount - cashPartValue);
  const splitValid = mode !== 'split' || (cashPart.trim() !== '' && cashPartValue >= 0 && upiPartValue >= 0);

  const pay = async () => {
    if (!splitValid) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.staffTabs.pay(tab.staff_id, {
        payment_mode: mode,
        ...(mode === 'split' ? { cash_amount: cashPartValue, upi_amount: upiPartValue } : {}),
      });
      const taken = result.payments[0]?.amount ?? tab.due_amount;
      toast.success(`${formatMoney(taken)} received from ${tab.name}. Staff tab settled.`);
      onPaid(result);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Settle ${tab.name}'s staff tab`} onClose={onClose}>
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <p className="text-sm text-slate-300">
          {tab.lines.length} item{tab.lines.length === 1 ? '' : 's'} at cost —{' '}
          <span className="font-semibold text-slate-100">{formatMoney(tab.due_amount)}</span>.
        </p>
        <PaymentModePicker mode={mode} onChange={setMode} />
        {mode === 'split' && (
          <SplitFields cashPart={cashPart} onCashPart={setCashPart} upiPart={upiPartValue} />
        )}
        <Button className="w-full" disabled={busy || !splitValid} onClick={pay}>
          {busy ? 'Recording…' : `Received ${formatMoney(tab.due_amount)}`}
        </Button>
      </div>
    </Modal>
  );
}

function LineRow({
  line,
  busy,
  onRemove,
}: {
  line: StaffTabLine;
  busy: boolean;
  onRemove?: (qty?: number) => void;
}) {
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-slate-100">
          {line.item_name}
          {line.qty > 1 && <span className="text-slate-400"> ×{line.qty}</span>}
        </p>
        <p className="text-xs text-slate-500">
          {formatMoney(line.unit_price)} each · {formatDateTime(line.added_at)}
          {line.added_by_name && ` · by ${line.added_by_name}`}
        </p>
      </div>
      <span className="tabular-nums text-slate-100">{formatMoney(line.line_total)}</span>
      {onRemove && (
        <div className="flex gap-1">
          {line.qty > 1 && (
            <Button variant="ghost" className="px-3" disabled={busy} onClick={() => onRemove(1)}>
              −1
            </Button>
          )}
          <Button variant="ghost" className="px-3 text-rose-300" disabled={busy} onClick={() => onRemove()}>
            Remove
          </Button>
        </div>
      )}
    </li>
  );
}

export function StaffTabPage() {
  const { staff: me } = useAuth();
  const toast = useToast();
  const [summaries, setSummaries] = useState<StaffTabSummary[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(me?.staff_id ?? null);
  const [tab, setTab] = useState<StaffTab | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [paying, setPaying] = useState(false);
  const [busyLine, setBusyLine] = useState<string | null>(null);

  const loadList = useCallback((signal?: AbortSignal) => {
    api.staffTabs
      .list(signal)
      .then(setSummaries)
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadList(controller.signal);
    return () => controller.abort();
  }, [loadList]);

  useEffect(() => {
    if (!selectedId) return;
    const controller = new AbortController();
    setTab(null);
    api.staffTabs
      .get(selectedId, controller.signal)
      .then(setTab)
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      });
    return () => controller.abort();
  }, [selectedId]);

  // Every change returns the whole tab; the list's totals follow.
  const applyTab = (next: StaffTab) => {
    setTab(next);
    loadList();
  };

  const remove = async (line: StaffTabLine, qty?: number) => {
    if (!tab) return;
    setBusyLine(line.tab_item_id);
    setError(null);
    try {
      applyTab(await api.staffTabs.removeItem(tab.staff_id, line.tab_item_id, qty));
      const removed = qty ?? line.qty;
      toast.success(`${removed === 1 ? line.item_name : `${line.item_name} ×${removed}`} taken off.`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyLine(null);
    }
  };

  const totalOwed = roundMoney((summaries ?? []).reduce((sum, row) => sum + row.due_amount, 0));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-lg font-semibold">Staff tab</h1>
        {summaries && (
          <p className="text-sm text-slate-400">
            {totalOwed > 0 ? `${formatMoney(totalOwed)} owed by staff` : 'Nobody owes anything'}
          </p>
        )}
      </div>
      <p className="text-sm text-slate-500">
        Food and drinks staff take on shift, charged at what the cafe paid. Pick a colleague, add what they
        took, and settle when they pay.
      </p>

      {error && <ErrorNote>{error}</ErrorNote>}

      {summaries === null && !error ? (
        <Spinner label="Loading staff tabs…" />
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[18rem_1fr]">
          <Card className="p-2">
            <ul className="space-y-1">
              {(summaries ?? []).map((row) => (
                <li key={row.staff_id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(row.staff_id)}
                    className={`flex min-h-11 w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                      selectedId === row.staff_id
                        ? 'bg-sky-500/15 text-sky-200'
                        : 'text-slate-200 hover:bg-slate-800'
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="truncate">{row.name}</span>
                      {row.staff_id === me?.staff_id && <Badge>You</Badge>}
                      {!row.is_active && <Badge tone="amber">Inactive</Badge>}
                    </span>
                    <span
                      className={`tabular-nums text-sm ${row.due_amount > 0 ? 'font-semibold text-amber-300' : 'text-slate-500'}`}
                    >
                      {row.due_amount > 0 ? formatMoney(row.due_amount) : '—'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          <div className="space-y-4">
            {!selectedId ? (
              <EmptyState title="Pick a staff member.">Their tab shows here.</EmptyState>
            ) : !tab ? (
              <Spinner label="Loading the tab…" />
            ) : (
              <>
                <Card className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h2 className="text-base font-semibold text-slate-100">{tab.name}</h2>
                      <p className="text-sm text-slate-400">
                        Owes{' '}
                        <span className={tab.due_amount > 0 ? 'font-semibold text-amber-300' : 'text-slate-300'}>
                          {formatMoney(tab.due_amount)}
                        </span>
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {tab.is_active && <Button onClick={() => setPicking(true)}>Add item</Button>}
                      {tab.due_amount > 0 && (
                        <Button variant="secondary" onClick={() => setPaying(true)}>
                          Settle
                        </Button>
                      )}
                    </div>
                  </div>
                  {!tab.is_active && (
                    <p className="text-sm text-amber-300">
                      This account is deactivated — nothing more can be added, but the tab can still be settled.
                    </p>
                  )}
                  {tab.lines.length === 0 ? (
                    <p className="text-sm text-slate-500">Nothing on the tab.</p>
                  ) : (
                    <ul className="divide-y divide-slate-800">
                      {tab.lines.map((line) => (
                        <LineRow
                          key={line.tab_item_id}
                          line={line}
                          busy={busyLine !== null}
                          onRemove={(qty) => remove(line, qty)}
                        />
                      ))}
                    </ul>
                  )}
                </Card>

                {tab.payments.length > 0 && (
                  <Card className="space-y-2">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Paid</h3>
                    <ul className="divide-y divide-slate-800">
                      {tab.payments.map((payment) => (
                        <li key={payment.payment_id} className="py-3">
                          <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <span className="font-medium text-slate-100">{formatMoney(payment.amount)}</span>
                            <span className="text-xs text-slate-500">
                              {payment.payment_mode === 'split'
                                ? `${formatMoney(payment.cash_amount)} cash + ${formatMoney(payment.upi_amount)} UPI`
                                : payment.payment_mode.toUpperCase()}{' '}
                              · {formatDateTime(payment.paid_at)}
                              {payment.received_by_name && ` · to ${payment.received_by_name}`}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-slate-500">
                            {payment.lines
                              .map((line) => (line.qty > 1 ? `${line.item_name} ×${line.qty}` : line.item_name))
                              .join(', ')}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </Card>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {picking && tab && (
        <MenuPickerDialog
          title={`Add to ${tab.name}'s staff tab`}
          // Only admins are sent purchase prices with the menu; staff see "Add"
          // and the cost appears on the line once it is on the tab.
          priceOf={(item) => item.purchase_price}
          onClose={() => setPicking(false)}
          onPick={async (item) => {
            const next = await api.staffTabs.addItem(tab.staff_id, item.item_id, 1);
            toast.success(`${item.name} added. ${tab.name} owes ${formatMoney(next.due_amount)}.`);
            applyTab(next);
          }}
        />
      )}
      {paying && tab && <PayStaffDialog tab={tab} onClose={() => setPaying(false)} onPaid={applyTab} />}
    </div>
  );
}
